import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import PlayerForm, { type PlayerFormValues } from '../components/players/PlayerForm'
import MeasurementsForm, {
  emptyMeasurements,
  type MeasurementsValues,
} from '../components/players/MeasurementsForm'
import FilmsForm, { type FilmInputRow } from '../components/players/FilmsForm'
import StringListForm from '../components/players/StringListForm'
import { BookOpen, Globe2 } from 'lucide-react'
import PhotoUpload from '../components/PhotoUpload'
import RemovePlayerModal from '../components/players/RemovePlayerModal'
import { apiJson } from '../lib/api'
import type { PlayerPosition } from '../types'

// /api/profile/:slug returns the full public profile including agent.id;
// we use that + the viewer's own agent.id (from /api/dashboard/agent) to
// gate the form on representing-agent ownership BEFORE rendering. The
// backend's requirePlayerOwner is the real protection (returns 403 on
// PUT or photo-upload), but loading the form for non-owners and only
// failing on submit was misleading UX — non-owners now bounce straight
// back to the public profile.
//
// Player-self editing (player.user_id === viewer.user.id) isn't gated
// here yet because v1 doesn't have a player-account login flow per
// spec §1. If that ships, extend this check to also accept player-self.

interface ProfileResponse {
  player: {
    id: string
    slug: string
    name: string
    position: PlayerPosition | null
    nationality: string | null
    university: string | null
    dob: string | null
    scouting_narrative: string | null
    photo_url: string | null
    api_sports_player_id: number | null
    // B12 — 'agent' | 'api_sports'. Branches the remove-player confirm
    // copy: hard delete vs detach back to Discover.
    source: string
    // B12a — derived from api_sports_player_id IS NOT NULL. Replaces the
    // source check for the RemovePlayerModal branch since represent-confirm
    // flips source to 'agent' on an imported player.
    is_imported: boolean
    // M7-socials — hydrate the inline form below.
    instagram_url: string | null
    youtube_url: string | null
    facebook_url: string | null
    // Tier-1 content batch — hydrate the statement + 3x3 sections below.
    statement: string | null
    is_3x3_active: boolean
  }
  agent: { id: string } | null
  // M5.3 — the public profile already surfaces the measurements
  // satellite. We hydrate MeasurementsForm from this block so the edit
  // page doesn't need a second round-trip.
  measurements: {
    height_cm: number | null
    weight_kg: number | null
    wingspan_cm: number | null
    standing_reach_cm: number | null
    max_vert_cm: number | null
    measured_at: string | null
    measured_by: string | null
  } | null
  // M7 films-hyperlinks (2026-06-07) — hydrate FilmsForm from the same
  // single profile-load round-trip the other satellites use.
  films: Array<{
    id: string
    url: string
    title: string | null
    created_at: string
  }>
  // Tier-1 content batch — plain string lists (replace-all on save).
  passports: string[]
  languages: string[]
}

interface SyncStatsResponse {
  ok: boolean
  seasons_attempted: number
  rows_upserted: number
  rate_limit_remaining: string | null
  rate_limit_limit: string | null
  per_season: Array<{
    season: number
    api_status: number
    api_results: number
    api_errors: unknown
    rows_returned: number
    rows_attributed: number
    rows_skipped_unknown_team: number
    leagues_aggregated: number
    skipped: boolean
  }>
  // Migration 0013 — daily sync cap (admins get null/null/true).
  sync_cap?: {
    limit: number | null
    used: number | null
    is_admin: boolean
  }
}

interface AgentDashboardLite {
  agent: { id: string }
}

interface UpdateResponse {
  id: string
  slug: string
}

// Map the PUT handler's per-field 400 keys (instagram_url_not_https,
// film_url_invalid, too_many_films, …) to one friendly line shown next to
// the Save button. Unknown keys pass through unchanged so we never swallow
// signal.
function friendlySaveError(key: string): string {
  if (key === 'too_many_films') return 'Up to 9 films per player.'
  if (key === 'too_many_passports') return 'Up to 3 passports per player.'
  if (key === 'too_many_languages') return 'Up to 6 languages per player.'
  if (key === 'statement_too_long') return 'The player statement is too long (1500 characters max).'
  if (key.endsWith('_not_https')) return 'Links must start with https://.'
  if (key === 'film_title_too_long') return 'A film title is too long (80 characters max).'
  if (key.startsWith('film_')) return 'Check the film links — at least one is invalid.'
  if (key.endsWith('_invalid')) return 'Check the links — at least one URL is malformed.'
  return key
}

// --- Socials: agents enter a handle; we store + render the full URL. -------
// SocialIcons and the API are unchanged — they still deal in full https
// URLs. This page just presents a handle UX: strip the domain on load
// (urlToHandle) and rebuild the canonical URL on save (handleToUrl).
type SocialPlatform = 'instagram' | 'youtube' | 'facebook'

const SOCIAL_BASE: Record<SocialPlatform, string> = {
  instagram: 'https://www.instagram.com/',
  // YouTube handles carry an @; the input prefix shows it, so the typed
  // value is the bare handle and we add the @ back here.
  youtube: 'https://www.youtube.com/@',
  facebook: 'https://www.facebook.com/',
}

// Stored URL → handle for the input. '' when empty/unset. Defensive against
// a value that isn't a parseable URL (returns it @-stripped, not a throw).
function urlToHandle(url: string | null): string {
  if (!url) return ''
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url.replace(/^@+/, '').trim()
  }
  const seg = parsed.pathname.split('/').filter(Boolean)[0] ?? ''
  return seg.replace(/^@+/, '')
}

// Handle (what the agent typed) → canonical https URL to store, or null when
// blank. If they paste a full URL we extract the handle first so we don't
// double up the domain. The server still validates the result is https.
function handleToUrl(platform: SocialPlatform, raw: string): string | null {
  let h = raw.trim()
  if (!h) return null
  if (/^https?:\/\//i.test(h)) h = urlToHandle(h)
  h = h.replace(/^@+/, '').replace(/^\/+/, '').replace(/\/+$/, '').trim()
  if (!h) return null
  return SOCIAL_BASE[platform] + h
}

// Format check for the handle inputs. Returns null when OK (well-formed or
// empty/cleared), or a short message to show inline + block the save on.
// We normalize the same way handleToUrl does, so a pasted URL is accepted
// (we extract its handle) rather than flagged as malformed.
function handleError(raw: string): string | null {
  let h = raw.trim()
  if (!h) return null
  if (/^https?:\/\//i.test(h)) h = urlToHandle(h)
  h = h.replace(/^@+/, '').replace(/^\/+/, '').replace(/\/+$/, '').trim()
  if (!h) return null
  if (/\s/.test(h)) return 'No spaces in a handle.'
  if (!/^[A-Za-z0-9._-]+$/.test(h)) return 'Use letters, numbers, and . _ - only.'
  return null
}

function SocialHandleField({
  label,
  prefix,
  value,
  onChange,
}: {
  label: string
  prefix: string
  value: string
  onChange: (v: string) => void
}) {
  const error = handleError(value)
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-hoop-text-3 mb-0.5">
        {label}
      </span>
      <div
        className={
          'flex items-stretch rounded-md border overflow-hidden bg-hoop-surface focus-within:ring-2 ' +
          (error
            ? 'border-hoop-red focus-within:ring-hoop-red'
            : 'border-hoop-border focus-within:ring-hoop-accent')
        }
      >
        <span className="flex items-center px-2.5 text-sm text-hoop-text-3 bg-hoop-bg border-r border-hoop-border select-none whitespace-nowrap">
          {prefix}
        </span>
        <input
          type="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="handle"
          aria-invalid={error ? true : undefined}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm bg-transparent text-hoop-text placeholder:text-hoop-text-3 outline-none"
        />
      </div>
      {error && (
        <p className="mt-0.5 text-[11px] text-hoop-red">{error}</p>
      )}
    </label>
  )
}

export default function PlayerEdit() {
  const navigate = useNavigate()
  const { slug } = useParams()
  const [playerId, setPlayerId] = useState<string | null>(null)
  const [initial, setInitial] = useState<PlayerFormValues | null>(null)
  // M5.3 — initial values for the MeasurementsForm. Hydrated from the
  // /api/profile/:slug measurements block during load(). Stays null
  // until the load completes so the form doesn't flash with empty
  // defaults that aren't the user's actual data.
  const [initialMeasurements, setInitialMeasurements] = useState<MeasurementsValues | null>(null)
  // M7 — films lifted into the page so the single "Save changes" button
  // persists them with the player fields. (Previously a separate "Save
  // films" button that silently no-op'd when the agent clicked only the
  // main Save — the bug this unification fixes.)
  const [films, setFilms] = useState<FilmInputRow[]>([])
  // M7-socials — three social HANDLES (keys keep the _url name but hold
  // bare handles now). Hydrated from the stored URLs via urlToHandle;
  // submit() rebuilds canonical URLs via handleToUrl. Saved by the
  // unified submit.
  const [socials, setSocials] = useState<{
    instagram_url: string
    youtube_url: string
    facebook_url: string
  }>({ instagram_url: '', youtube_url: '', facebook_url: '' })
  // Tier-1 content batch — statement + 3x3 tag + the two string lists,
  // lifted to page state (same pattern as films/socials) so the single
  // "Save changes" persists everything together.
  const [statement, setStatement] = useState('')
  const [is3x3Active, setIs3x3Active] = useState(false)
  const [passports, setPassports] = useState<string[]>([])
  const [languages, setLanguages] = useState<string[]>([])
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // Phase A1 — track the SAVED api_sports_player_id (vs. the form's
  // editable value) so the Sync button knows whether the server-side
  // column is populated. Updated when the load completes and after a
  // successful PUT.
  const [savedApiSportsPlayerId, setSavedApiSportsPlayerId] = useState<number | null>(null)
  // Sync run state for the "Sync stats now" panel.
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<SyncStatsResponse | null>(null)
  const [syncErr, setSyncErr] = useState<string | null>(null)
  // Inline post-save signal so the user knows their changes landed
  // without us tearing the page out from under them — needed because
  // we no longer navigate away on save (the Sync panel sits below).
  const [saveOk, setSaveOk] = useState(false)
  // B12/B12a — remove-player danger zone. `isImported` picks the modal copy
  // branch (hard delete vs detach); deliberately edit-page-only friction, no
  // shortcut from the dashboard roster. Tracks is_imported from the profile
  // response, NOT source (which flips to 'agent' post-confirm).
  const [isImported, setIsImported] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      navigate('/dashboard', { replace: true })
      return
    }
    async function load() {
      try {
        // 1. Public profile gives us the player + the representing agent's id.
        const data = await apiJson<ProfileResponse>(
          `/api/profile/${encodeURIComponent(slug!)}`,
        )
        if (cancelled) return
        const playerAgentId = data.agent?.id ?? null

        // 2. Viewer's agent id (will 403 for non-agents — treat as not-owner).
        let viewerAgentId: string | null = null
        try {
          const dashboard = await apiJson<AgentDashboardLite>('/api/dashboard/agent')
          if (cancelled) return
          viewerAgentId = dashboard.agent?.id ?? null
        } catch {
          viewerAgentId = null
        }

        // 3. Ownership gate. If you're not the representing agent, bounce.
        if (!playerAgentId || !viewerAgentId || playerAgentId !== viewerAgentId) {
          navigate(`/players/${encodeURIComponent(slug!)}`, { replace: true })
          return
        }

        setPlayerId(data.player.id)
        setPhotoUrl(data.player.photo_url)
        setIsImported(data.player.is_imported)
        setSavedApiSportsPlayerId(data.player.api_sports_player_id)
        setInitial({
          name: data.player.name,
          position: data.player.position ?? '',
          nationality: data.player.nationality ?? '',
          university: data.player.university ?? '',
          dob: data.player.dob ?? '',
          scouting_narrative: data.player.scouting_narrative ?? '',
          api_sports_player_id:
            data.player.api_sports_player_id != null
              ? String(data.player.api_sports_player_id)
              : '',
        })
        // Hydrate the measurements form from the satellite row. When
        // the row doesn't exist yet, fall through to emptyMeasurements
        // so the user sees blank inputs.
        if (data.measurements) {
          setInitialMeasurements({
            height_cm: data.measurements.height_cm != null ? String(data.measurements.height_cm) : '',
            weight_kg: data.measurements.weight_kg != null ? String(data.measurements.weight_kg) : '',
            wingspan_cm: data.measurements.wingspan_cm != null ? String(data.measurements.wingspan_cm) : '',
            standing_reach_cm: data.measurements.standing_reach_cm != null ? String(data.measurements.standing_reach_cm) : '',
            max_vert_cm: data.measurements.max_vert_cm != null ? String(data.measurements.max_vert_cm) : '',
            measured_at: data.measurements.measured_at ?? '',
            measured_by: data.measurements.measured_by ?? '',
          })
        } else {
          setInitialMeasurements(emptyMeasurements)
        }
        // M7 — hydrate films into page state. The unified save re-sends
        // these verbatim when untouched, so existing films are preserved.
        setFilms(
          (data.films ?? []).map((f) => ({
            url: f.url,
            title: f.title ?? '',
          })),
        )
        // Tier-1 content batch — hydrate statement, 3x3 tag, and the
        // string lists from the same profile round-trip.
        setStatement(data.player.statement ?? '')
        setIs3x3Active(data.player.is_3x3_active)
        setPassports(data.passports ?? [])
        setLanguages(data.languages ?? [])
        // M7-socials — hydrate the inputs as handles extracted from the
        // stored URLs. The agent edits handles; submit() rebuilds URLs.
        setSocials({
          instagram_url: urlToHandle(data.player.instagram_url),
          youtube_url: urlToHandle(data.player.youtube_url),
          facebook_url: urlToHandle(data.player.facebook_url),
        })
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'unknown_error'
        if (msg === 'unauthorized') {
          navigate('/', { replace: true })
          return
        }
        setErr(msg)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug, navigate])

  async function submit(values: PlayerFormValues) {
    if (!playerId) throw new Error('not_loaded')
    if (!values.position) throw new Error('position_required')
    // Form value is a string; parse to integer / null for the PUT body.
    const apiSportsRaw = values.api_sports_player_id.trim()
    let apiSportsParsed: number | null = null
    if (apiSportsRaw !== '') {
      const parsed = Number(apiSportsRaw)
      if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error('api_sports_player_id_invalid')
      }
      apiSportsParsed = parsed
    }
    // Tier-1 content batch — mirror the server's statement cap client-side
    // so the inline counter and the save error agree.
    if (statement.trim().length > 1500) {
      throw new Error(friendlySaveError('statement_too_long'))
    }
    // Block the save if any social handle is malformed (the inline field
    // errors point at which one). Empty handles are fine (cleared).
    for (const [name, val] of [
      ['Instagram', socials.instagram_url],
      ['YouTube', socials.youtube_url],
      ['Facebook', socials.facebook_url],
    ] as const) {
      const he = handleError(val)
      if (he) throw new Error(`${name} handle: ${he}`)
    }
    // M7 — one PUT persists the player fields, socials, AND films together
    // so nothing is silently dropped. Socials: empty-after-trim → null
    // (clear). Films: drop wholly-empty rows so a stray "Add film" click
    // doesn't block the save; the endpoint replaces the film set atomically.
    const filmsPayload = films
      .filter((r) => r.url.trim() || r.title.trim())
      .map((r) => {
        const title = r.title.trim()
        return title ? { url: r.url.trim(), title } : { url: r.url.trim() }
      })
    try {
      await apiJson<UpdateResponse>(`/api/players/${playerId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: values.name.trim(),
          position: values.position,
          nationality: values.nationality.trim(),
          university: values.university.trim() || null,
          dob: values.dob.trim() || null,
          scouting_narrative: values.scouting_narrative.trim() || null,
          api_sports_player_id: apiSportsParsed,
          // Socials are entered as handles; rebuild the canonical URL to
          // store. The server still validates it's a valid https URL.
          instagram_url: handleToUrl('instagram', socials.instagram_url),
          youtube_url: handleToUrl('youtube', socials.youtube_url),
          facebook_url: handleToUrl('facebook', socials.facebook_url),
          films: filmsPayload,
          // Tier-1 content batch — statement (empty → clear), 3x3 tag,
          // and the two replace-all lists. Blank rows are dropped here
          // (same spirit as the films filter); the server re-trims,
          // dedupes, and enforces the caps.
          statement: statement.trim() || null,
          is_3x3_active: is3x3Active,
          passports: passports.map((p) => p.trim()).filter(Boolean),
          languages: languages.map((l) => l.trim()).filter(Boolean),
        }),
      })
    } catch (e) {
      // Surface the server's per-field validation errors in friendly form
      // next to the Save button (PlayerForm renders whatever onSubmit throws).
      throw new Error(friendlySaveError(e instanceof Error ? e.message : 'save_failed'))
    }
    // Stay on the page — the Sync panel below needs the freshly-saved
    // api_sports_player_id. Surface the inline "Saved" indicator.
    setSavedApiSportsPlayerId(apiSportsParsed)
    setSaveOk(true)
    setSyncResult(null)
    setSyncErr(null)
    window.setTimeout(() => setSaveOk(false), 2500)
  }

  // B12 — DELETE the player (backend branches hard-delete vs detach on
  // source). Throws for the modal to surface inline; on success navigates
  // to the dashboard with a flash notice describing which branch ran.
  async function removePlayer() {
    if (!playerId) throw new Error('not_loaded')
    const name = initial?.name ?? 'Player'
    const res = await apiJson<{ deleted?: boolean; detached?: boolean }>(
      `/api/players/${playerId}`,
      { method: 'DELETE' },
    )
    navigate('/dashboard', {
      state: {
        flash: res.deleted
          ? `${name} was permanently deleted.`
          : `${name} was removed from your roster and returned to Discover.`,
      },
    })
  }

  async function runSync() {
    if (!playerId || savedApiSportsPlayerId == null || syncing) return
    setSyncing(true)
    setSyncResult(null)
    setSyncErr(null)
    try {
      const res = await apiJson<SyncStatsResponse>(
        `/api/players/${playerId}/sync-stats`,
        { method: 'POST' },
      )
      setSyncResult(res)
    } catch (e) {
      // Migration 0013 — friendly 429 message with the daily limit.
      if (e instanceof Error && e.message === 'daily_sync_cap') {
        setSyncErr('daily_sync_cap')
      } else {
        setSyncErr(e instanceof Error ? e.message : 'sync_failed')
      }
    } finally {
      setSyncing(false)
    }
  }

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-10">
        <p className="text-sm text-hoop-red">{err}</p>
      </div>
    )
  }
  if (!initial) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-10 text-hoop-text-3 text-sm">
        Loading…
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-hoop-text mb-1">
        Edit player
      </h1>
      <p className="text-hoop-text-3 text-sm mb-8">
        Updates publish immediately.
      </p>
      {playerId && (
        <div className="mb-8">
          <PhotoUpload
            entityType="player"
            entityId={playerId}
            currentPhotoUrl={photoUrl}
            onChange={setPhotoUrl}
            shape="square"
            label="Player headshot"
          />
        </div>
      )}
      <PlayerForm
        initial={initial}
        submitLabel="Save changes"
        onSubmit={submit}
        onCancel={() => navigate(`/players/${slug ?? ''}`)}
      >
        {/* M7-socials — handle inputs (we store + render the full URL).
            LinkedIn is NOT here — product decision per the 2026-06-07 call. */}
        <section className="rounded-lg border border-hoop-border bg-hoop-bg p-5">
          <h2 className="text-sm font-semibold text-hoop-text mb-1">
            Socials
          </h2>
          <p className="text-xs text-hoop-text-3 mb-4">
            Just the handle — we build the link. Leave a field blank to clear it.
          </p>
          <div className="space-y-3">
            <SocialHandleField
              label="Instagram"
              prefix="instagram.com/"
              value={socials.instagram_url}
              onChange={(v) => setSocials((s) => ({ ...s, instagram_url: v }))}
            />
            <SocialHandleField
              label="YouTube"
              prefix="youtube.com/@"
              value={socials.youtube_url}
              onChange={(v) => setSocials((s) => ({ ...s, youtube_url: v }))}
            />
            <SocialHandleField
              label="Facebook"
              prefix="facebook.com/"
              value={socials.facebook_url}
              onChange={(v) => setSocials((s) => ({ ...s, facebook_url: v }))}
            />
          </div>
        </section>

        {/* Tier-1 content batch — first-person player statement with a
            live character counter (server cap: 1500). */}
        <section className="rounded-lg border border-hoop-border bg-hoop-bg p-5">
          <h2 className="text-sm font-semibold text-hoop-text mb-1">
            Player statement
          </h2>
          <p className="text-xs text-hoop-text-3 mb-4">
            First-person, in the player's own words — distinct from the
            scouting narrative above. Shown as its own section on the public
            profile.
          </p>
          <textarea
            rows={5}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="I grew up playing on outdoor courts in…"
            className="w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
          />
          <p
            className={
              'mt-1 text-right text-[11px] ' +
              (statement.trim().length > 1500 ? 'text-hoop-red' : 'text-hoop-text-3')
            }
          >
            {statement.trim().length}/1500
          </p>
        </section>

        {/* Tier-1 content batch — passports + languages list editors
            (replace-all on save, caps server-enforced). */}
        <StringListForm
          title="Passports"
          description="Countries the player holds a passport for."
          icon={BookOpen}
          addLabel="Add passport"
          placeholder="e.g. France"
          max={3}
          rows={passports}
          onChange={setPassports}
        />
        <StringListForm
          title="Languages spoken"
          description="Languages the player can communicate in."
          icon={Globe2}
          addLabel="Add language"
          placeholder="e.g. Spanish"
          max={6}
          rows={languages}
          onChange={setLanguages}
        />

        {/* Tier-1 content batch — manual 3x3 tag. */}
        <section className="rounded-lg border border-hoop-border bg-hoop-bg p-5">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-hoop-text">
            <input
              type="checkbox"
              checked={is3x3Active}
              onChange={(e) => setIs3x3Active(e.target.checked)}
              className="rounded border-hoop-border accent-hoop-primary"
            />
            <span className="font-semibold">3x3-active</span>
          </label>
          <p className="mt-1 text-xs text-hoop-text-3">
            Shows a 3x3 badge on the profile and makes the player findable
            via the 3x3 filter on Discover.
          </p>
        </section>

        {/* M7 — film hyperlinks editor. Controlled by `films` state and
            persisted by the same submit. */}
        <FilmsForm rows={films} onChange={setFilms} />
      </PlayerForm>

      {/* M5.3 — separate satellite form (its own /measurements endpoint
          and save button); left independent of the unified player save. */}
      {playerId && initialMeasurements && (
        <MeasurementsForm playerId={playerId} initial={initialMeasurements} />
      )}

      {saveOk && (
        <p className="mt-3 text-sm text-hoop-green">
          Saved.{' '}
          <Link
            to={`/players/${slug ?? ''}`}
            className="underline hover:text-hoop-green"
          >
            Back to profile →
          </Link>
        </p>
      )}

      {savedApiSportsPlayerId != null && (
        <section className="mt-8 rounded-lg border border-hoop-border bg-hoop-bg p-5">
          <h2 className="text-sm font-semibold text-hoop-text mb-1">
            API-Sports stats sync
          </h2>
          <p className="text-xs text-hoop-text-3 mb-3">
            Pulls per-game lines from <code>v1.basketball.api-sports.io</code> for
            seasons 2023–2026 and aggregates to per-season rows in this player's
            career stats. Coverage is currently best for Thailand TBL (2023–2024)
            and Vietnam VBA (2023–2025); other season/league combos return empty
            and are skipped without error.
          </p>
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            className="bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
          >
            {syncing ? 'Syncing…' : 'Sync stats now'}
          </button>

          {syncErr && (
            <p className="mt-3 text-sm text-hoop-red">
              {syncErr === 'daily_sync_cap'
                ? 'Daily sync limit reached — try again tomorrow'
                : syncErr}
            </p>
          )}

          {syncResult && (
            <div className="mt-4 space-y-2 text-xs">
              <p className="text-hoop-text-2">
                <strong>{syncResult.rows_upserted}</strong> rows upserted across
                {' '}{syncResult.seasons_attempted} season(s).
                {syncResult.rate_limit_remaining != null && (
                  <>
                    {' · '}rate limit remaining:{' '}
                    <span className="font-mono">{syncResult.rate_limit_remaining}</span>
                    {syncResult.rate_limit_limit != null && (
                      <> / {syncResult.rate_limit_limit}</>
                    )}
                  </>
                )}
                {syncResult.sync_cap &&
                  syncResult.sync_cap.limit != null &&
                  syncResult.sync_cap.used != null && (
                    <>
                      {' · '}
                      <span className="text-hoop-text-3">
                        {syncResult.sync_cap.used} of {syncResult.sync_cap.limit} daily syncs used
                      </span>
                    </>
                  )}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-hoop-text-3 border-b border-hoop-border">
                      <th className="py-1 pr-3">Season</th>
                      <th className="py-1 pr-3">HTTP</th>
                      <th className="py-1 pr-3" title="Total per-game rows returned by /games/statistics/players">Rows</th>
                      <th className="py-1 pr-3" title="Rows attributed to a supported league (TBL or VBA)">Attrib.</th>
                      <th className="py-1 pr-3" title="Rows from teams not in TBL/VBA — silently skipped">Skipped (other league)</th>
                      <th className="py-1 pr-3">Leagues</th>
                      <th className="py-1">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncResult.per_season.map((s) => (
                      <tr key={s.season} className="border-b border-hoop-border last:border-b-0">
                        <td className="py-1 pr-3 font-mono">{s.season}</td>
                        <td className="py-1 pr-3 font-mono">{s.api_status}</td>
                        <td className="py-1 pr-3 font-mono">{s.rows_returned}</td>
                        <td className="py-1 pr-3 font-mono">{s.rows_attributed}</td>
                        <td className="py-1 pr-3 font-mono">{s.rows_skipped_unknown_team}</td>
                        <td className="py-1 pr-3 font-mono">{s.leagues_aggregated}</td>
                        <td className="py-1 text-hoop-text-3 truncate max-w-[200px]">
                          {s.api_errors && JSON.stringify(s.api_errors) !== '[]'
                            ? JSON.stringify(s.api_errors)
                            : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* B12/B12a — danger zone. Deliberately the ONLY remove affordance (no
          dashboard shortcut). Copy + button branch on is_imported (NOT source,
          which flips post-confirm) via the modal. */}
      <section className="mt-8 rounded-lg border border-hoop-red-bg bg-hoop-bg p-5">
        <h2 className="text-sm font-semibold text-hoop-red mb-1">
          Danger zone
        </h2>
        <p className="text-xs text-hoop-text-3 mb-3">
          {isImported
            ? 'This player was imported from league data. Removing them takes them off your roster and returns their profile to Discover.'
            : 'This player profile was created by you. Removing it deletes the profile and everything attached to it, permanently.'}
        </p>
        <button
          type="button"
          onClick={() => setRemoveOpen(true)}
          className="px-4 py-2 rounded-md text-sm font-medium border border-hoop-red text-hoop-red hover:bg-hoop-red-bg"
        >
          Remove player
        </button>
      </section>

      <RemovePlayerModal
        open={removeOpen}
        playerName={initial.name}
        isAgentCreated={!isImported}
        onClose={() => setRemoveOpen(false)}
        onConfirm={removePlayer}
      />
    </div>
  )
}
